import type {
	AppID,
	CartValidation,
	NubeNavigateEventData,
	NubeSDKRuntime,
	NubeSDKSendableEvent,
	NubeSdkInternalEvent,
	NubeSdkInternalEventData,
	NubeStorageGetResponseEventData,
	NubeStorageId,
	NubeStorageQueryEventData,
	NubeStorageRemoveResponseEventData,
	NubeStorageSetEventData,
	NubeStorageSetResponseEventData,
	WorkerInternalMessage,
	WorkerMessage,
	WorkerMessageHandlerFunc,
} from "@tiendanube/nube-sdk-internal-types";
import { handleSlotSet } from "./slot-set";
import { handleTelemetry } from "./telemetry";
import type { WorkerTelemetryMessage } from "./telemetry/telemetry.ts";

export function parseCartValidation(value: unknown): CartValidation {
	const val = value as CartValidation | undefined;

	if (!val) return { status: "pending" };

	switch (val.status) {
		case "fail":
			if (typeof val.reason === "string")
				return { status: "fail", reason: val.reason };
			break;

		case "success":
			return { status: "success" };
	}

	return { status: "pending" };
}

export const handleCartAddCoupon: WorkerMessageHandlerFunc = (
	{ changes },
	state,
) => {
	const newCode = changes?.cart?.coupon?.code;
	const currentCode = state.cart?.coupon?.code;

	if (newCode === undefined || newCode === currentCode) {
		return null;
	}

	return {
		cart: {
			coupon: {
				code: newCode,
			},
		},
	};
};

export const handleCartRemoveCoupon: WorkerMessageHandlerFunc = (
	{ changes },
	state,
) => {
	const currentCode = state.cart?.coupon?.code;
	return !currentCode || changes ? null : {};
};

export const handleShippingSelection: WorkerMessageHandlerFunc = (
	{ changes },
	{ shipping },
) => {
	if (changes?.shipping?.selected === shipping?.selected) return null;

	const selectedOption = changes?.shipping?.selected;

	if (!selectedOption) return null;

	const hasValidOption = Boolean(
		shipping?.options?.find((option) => option.code === selectedOption),
	);

	if (!hasValidOption) return null;

	return {
		shipping: {
			options: shipping?.options || [],
			custom_labels: shipping?.custom_labels || {},
			selected: selectedOption,
		},
	};
};

export const handleConfigSet: WorkerMessageHandlerFunc = ({
	app,
	changes,
}) => ({
	apps: {
		[app]: {
			config: {
				has_cart_validation: changes?.config?.has_cart_validation || false,
				disable_shipping_more_options:
					changes?.config?.disable_shipping_more_options || false,
			},
		},
	},
});

export const handleWebWorkerError: WorkerMessageHandlerFunc = (
	{ app, error },
	state,
) => {
	const currentErrors = state.apps?.[app]?.errors || [];
	const newErrors = error ? [...currentErrors, error] : currentErrors;

	return {
		apps: {
			[app]: {
				errors: newErrors,
			},
		},
	};
};

export const handleCartValidation: WorkerMessageHandlerFunc = ({
	app,
	changes,
}) => ({
	apps: {
		[app]: {
			cart: {
				validation: parseCartValidation(changes?.cart?.validation),
			},
		},
	},
});

const handlers = new Map<
	NubeSDKSendableEvent | "WebWorkerError",
	WorkerMessageHandlerFunc
>([
	["config:set", handleConfigSet],
	["cart:validate", handleCartValidation],
	["coupon:add", handleCartAddCoupon],
	["coupon:remove", handleCartRemoveCoupon],
	["shipping:select", handleShippingSelection],
	["ui:slot:set", handleSlotSet],
	["WebWorkerError", handleWebWorkerError],
]);

export function createWorkerMessageHandler(
	handlers: Map<
		NubeSDKSendableEvent | "WebWorkerError",
		WorkerMessageHandlerFunc
	>,
) {
	return (message: WorkerMessage, runtime: NubeSDKRuntime) => {
		const { app, type, changes } = message;
		const handler = handlers.get(type);

		if (handler) {
			const state = runtime.getState();
			const result = handler(message, state);
			if (result) {
				runtime.send(app, type, () => result);
			}
			return;
		}

		if (changes) {
			runtime.send(app, type, () => changes);
			return;
		}

		runtime.send(app, type);
	};
}

export const workerMessageHandler = createWorkerMessageHandler(handlers);

export type WorkerInternalMessageOptions = {
	localStorage: Storage;
	sessionStorage: Storage;
};

export function buildAppStorageKey(appId: AppID, key: string) {
	return `app-${appId}-${key}`;
}

export function encodeAppStorageValue(
	value: string | null,
	ttl?: number,
	now: number = Date.now(),
) {
	const data = {
		value,
		...(ttl !== undefined ? { ttl: now + ttl * 1000 } : {}),
	};
	return JSON.stringify(data);
}

export function decodeAppStorageValue(
	storageValue: string | null,
	now = Date.now(),
): string | null {
	if (!storageValue) return null;
	try {
		const data = JSON.parse(storageValue);

		if (!data) {
			return null;
		}

		if (typeof data !== "object") {
			console.error(
				"Invalid value stored in storage, maybe it was modified from outside the SDK",
				storageValue,
			);
			return null;
		}

		if (data?.ttl) {
			// Has expiration date, validate
			if (typeof data.ttl !== "number") {
				console.error(
					"Invalid value stored in storage, maybe it was modified from outside the SDK",
					storageValue,
				);
				return null;
			}
			const isExpired = !!data.ttl && data.ttl < now;
			if (isExpired) return null;
		}

		if (data.value && typeof data.value !== "string") {
			console.error(
				"Invalid value stored in storage, maybe it was modified from outside the SDK",
				storageValue,
			);
			return null;
		}

		if (data.value === undefined) return null;

		return data.value;
	} catch (err) {
		console.error(
			"Invalid value stored in storage, maybe it was modified from outside the SDK",
			storageValue,
			err,
		);
		return null;
	}
}

export function isInternalUrl(
	url: string,
	baseUrl: string,
): {
	status: boolean;
	error?: string;
} {
	try {
		if (url.startsWith("http://") || url.startsWith("https://")) {
			const urlObj = new URL(url);
			const baseUrlObj = new URL(baseUrl);

			if (urlObj.hostname !== baseUrlObj.hostname) {
				return {
					status: false,
					error: "External URL detected",
				};
			}

			if (
				urlObj.pathname === baseUrlObj.pathname &&
				urlObj.search === baseUrlObj.search
			) {
				return {
					status: false,
					error: "Already on target URL",
				};
			}

			return { status: true };
		}

		if (!url.startsWith("/") && !url.startsWith("./")) {
			return {
				status: false,
				error: "Invalid relative URL format",
			};
		}

		const normalizedUrl = new URL(url, baseUrl);
		const normalizedBaseUrl = new URL(baseUrl);

		if (
			normalizedUrl.pathname === normalizedBaseUrl.pathname &&
			normalizedUrl.search === normalizedBaseUrl.search
		) {
			return {
				status: false,
				error: "Already on target URL",
			};
		}

		return { status: true };
	} catch {
		return {
			status: false,
			error: "Invalid URL format",
		};
	}
}

export function createWorkerTelemetryMessageHandler(): (
	sessionId: string,
	message: WorkerTelemetryMessage,
) => void {
	return (sessionId: string, message: WorkerTelemetryMessage) => {
		handleTelemetry(sessionId, message);
	};
}

export function createWorkerInternalMessageHandler(
	appId: AppID,
	worker: Worker,
	options: WorkerInternalMessageOptions,
): (message: WorkerInternalMessage, runtime: NubeSDKRuntime) => void {
	function getStorage(storageId: NubeStorageId) {
		switch (storageId) {
			case "session-storage":
				return options.sessionStorage;
			case "local-storage":
				return options.localStorage;
		}
	}

	function postMessage(
		eventId: NubeSdkInternalEvent,
		data: NubeSdkInternalEventData,
	) {
		worker.postMessage(
			JSON.stringify({
				type: eventId,
				data: data,
			}),
		);
	}

	function isEventStorageQueryEvent(event: WorkerInternalMessage): boolean {
		return (
			event.type === "internal:storage:get" ||
			event.type === "internal:storage:set" ||
			event.type === "internal:storage:remove"
		);
	}

	function isNavigateEvent(event: WorkerInternalMessage): boolean {
		return event.type === "internal:navigate";
	}

	return (message, runtime) => {
		if (isNavigateEvent(message)) {
			const data = message.data as NubeNavigateEventData;
			const route = data.route;
			const { url } = runtime.getState().location;

			const result = isInternalUrl(route, url);
			if (result.status) {
				window.location.href = route;
				return;
			}

			runtime.send("checkout", "WebWorkerError", () => ({
				apps: {
					[appId]: {
						errors: [
							...(runtime.getState().apps?.[appId]?.errors || []),
							{
								code: "navigation_error",
								message: result.error,
							},
						],
					},
				},
			}));
		}

		if (isEventStorageQueryEvent(message)) {
			const data = message.data as NubeStorageQueryEventData;
			const storage = getStorage(data.storageId);
			const storageKey = buildAppStorageKey(appId, data.key);

			switch (message.type) {
				case "internal:storage:get": {
					const storageValue = storage.getItem(storageKey);
					const value = decodeAppStorageValue(storageValue);
					const response: NubeStorageGetResponseEventData = {
						storageId: data.storageId,
						key: data.key,
						requestId: data.requestId,
						value,
					};
					postMessage("internal:storage:get:response", response);
					break;
				}

				case "internal:storage:set": {
					const setEventData = data as NubeStorageSetEventData;
					const finalValue = encodeAppStorageValue(
						setEventData.value,
						setEventData.ttl,
					);
					try {
						storage.setItem(storageKey, finalValue);
					} catch (err) {
						// Setting an item in storage can fail if the storage is full, we log the error but we don't propagate it to the worker.. maybe
						// this should be propagated?
						console.error(err);
					}
					const response: NubeStorageSetResponseEventData = {
						storageId: data.storageId,
						requestId: data.requestId,
					};
					postMessage("internal:storage:set:response", response);
					break;
				}

				case "internal:storage:remove": {
					storage.removeItem(storageKey);
					const response: NubeStorageRemoveResponseEventData = {
						storageId: data.storageId,
						requestId: data.requestId,
					};
					postMessage("internal:storage:remove:response", response);
					break;
				}
			}
		}
	};
}

export function createWorker(...parts: string[]): Worker {
	const blob = new Blob(parts, { type: "application/javascript" });
	const worker = new Worker(URL.createObjectURL(blob));
	return worker;
}
